<template>
  <div
    v-if="headline || text"
  >
    <div class="product-content-block">
      <div class="product-content-block__wrapper">
        <div class="product-content-block__wrapper-text">
          <div class="product-content-block__wrapper-text-inner">
            <h2
              class="product-content-block__headline headline headline--type_h2"
              v-if="headline"
              v-html="headline"
            />
            <h3
              class="product-content-block__subline"
              v-if="subline"
              v-html="subline"
            />
            <component
              :is="useReadMore ? 'read-more' : 'div'"
              class="product-content-block__read-more"
              text-more="ads.main.detail.read_more"
              text-less="ads.main.detail.read_less"
              :max-height="settings.readMoreHeight"
              :scroll-less-element="`.${readMoreTargetClass}`"
            >
              <div
                class="product-content-block__description"
                v-if="text"
                v-html="text"
              />
            </component>
            <div
              class="product-content-block__button"
              v-if="link1Text || link2Text"
            >
              <div class="product-content-block__button-inner">
                <a
                  class="button button--text button--link-arrow product-content-block__button-link"
                  :href="link1Url"
                  v-if="link1Text"
                >
                  {{ link1Text }}
                </a>
                <a
                  class="button button--text button--link-arrow product-content-block__button-link"
                  :href="link2Url"
                  v-if="link2Text"
                >
                  {{ link2Text }}
                </a>
              </div>
            </div>
          </div>
        </div>
        <lazy-load
          v-if="carouselItems.length > 0"
          class="product-content-block__wrapper-image"
          :class="{
            'product-content-block__wrapper-image--one-slide': carouselItems.length < 2
          }"
        >
          <div
            class="product-content-block__carousel"
            v-if="showCarousel"
          >
            <carousel
              class="product-content-block__carousel-images"
              :slides-count="carouselItems.length"
              :slides-per-view-desktop="1"
              :slides-per-view-mobile="1"
              :slides-per-view-tablet="1"
              :show-pagination="true"
              :lazy-loading="true"
              :preload-slide-count="0"
              @change="onCarouselChange"
              @created="onCarouselChange(0)"
            >
              <template
                v-for="(item, index) in carouselItems"
                :key="`product-content-block-${index}`"
                #[`slide-${index}`]
              >
                <lazy-load
                  :class="`product-content-block__image-wrapper slide-${index}`"
                >
                  <image-element
                    v-if="!item.videoId"
                    class="product-content-block__picture"
                    img-class="product-content-block__image product-content-block__image--auto-center"
                    :src-mobile="[item.bigMobile]"
                    :src="[item.big]"
                    :alt="headline"
                    loading="eager"
                    :webp="useWebp(item.big)"
                  />
                  <video-player
                    v-else-if="item.videoId"
                    class="product-content-block__video"
                    :video-data="item"
                    @play-from-start="trackVideoPlay(item)"
                    ref="videoPlayersRef"
                  />
                </lazy-load>
              </template>
            </carousel>
          </div>
          <div
            class="product-content-block__images"
            v-else
          >
            <div
              v-for="(image, index) in images"
              :key="`product-content-block-${index}`"
              :class="`product-content-block__image-wrapper slide-${index}`"
            >
              <image-element
                class="product-content-block__picture"
                img-class="product-content-block__image product-content-block__image--auto-center"
                :src-mobile="[image.bigMobile]"
                :src="[image.big]"
                :alt="headline"
                :webp="useWebp(image.big)"
              />
            </div>
          </div>
        </lazy-load>
        <lazy-load
          class="product-content-block__wrapper-image"
          v-else-if="!images || images.length <= 0 && fallbackImage"
        >
          <image-element
            class="product-content-block__picture"
            img-class="product-content-block__image product-content-block__image--auto-center"
            :src-mobile="[fallbackImage]"
            :src="[fallbackImage]"
            :alt="headline"
          />
        </lazy-load>
        <div
          v-else
          class="product-content-block__wrapper-image product-content-block__wrapper-image--empty"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import {Vue, Options, Prop, Ref} from 'vue-property-decorator';
  import {defineAsyncComponent} from 'vue';
  import carousel from '../../components/carousel/carousel.vue';
  import ReadMore from '../readMore/readMore.vue';
  import LazyLoad from '../lazyLoad/lazyLoad.vue';
  import ImageElement from '../imageElement/imageElement.vue';
  import type VideoViewmodel from '../../types/VideoViewmodel';
  import type VideoPlayerType from '../videoPlayer/videoPlayer.vue';
  import {ProductContentBlockSettings} from './productContentBlockSettings';
  import diContainer from '../../utilities/js/diContainer/diContainer';
  import tracking from '../../utilities/js/tracking/Tracking';

  const VideoPlayer = defineAsyncComponent(() => import('../videoPlayer/videoPlayer.vue'));

  // Displays contents, used on 1. detail page and 2. category page (bottom)

  @Options({
    name: 'product-content-block',
    components: {
      ImageElement,
      LazyLoad,
      carousel,
      ReadMore,
      VideoPlayer
    }
  })
  export default class ProductContentBlock extends Vue {
    @Prop({default: ''})
    readonly headline: string;
    @Prop({default: ''})
    readonly subline: string;
    @Prop({default: ''})
    readonly text: string;
    @Prop({default: ''})
    readonly link1Text: string;
    @Prop({default: ''})
    readonly link1Url: string;
    @Prop({default: ''})
    readonly link2Text: string;
    @Prop({default: ''})
    readonly link2Url: string;
    @Prop({default: () => []})
    readonly images: Array<{ big: string, bigMobile: string }>;
    @Prop({default: () => []})
    readonly videos: Array<VideoViewmodel>;
    @Prop({default: false})
    readonly showCarousel: boolean;
    @Prop()
    readonly fallbackImage: string;
    @Prop()
    readonly readMoreTargetClass: string;
    @Prop({default: ''})
    readonly type: string
    @Prop({default: true})
    useReadMore: boolean;

    @Ref()
    readonly videoPlayersRef: VideoPlayerType[];

    settings: ProductContentBlockSettings = diContainer.get('productContentBlockSettings');

    get carouselItems() {
      return [...this.videos, ...this.images]
    }

    stopAllPlayers() {
      if (this.videoPlayersRef && this.videoPlayersRef.length) {
        this.videoPlayersRef.forEach((player) => {
          if (player.player) {
            player.player.pause();
          }
        })
      }
    }

    onCarouselChange(slideIndex: number) {
      this.stopAllPlayers();
      if ('videoId' in this.carouselItems[slideIndex]) {
        this.trackVideoImpression(this.carouselItems[slideIndex] as VideoViewmodel);
      }
    }

    trackVideoPlay(videoData: VideoViewmodel) {
      tracking.fireVideoPlay(videoData, `content.${this.type}`);
    }

    trackVideoImpression(videoData: VideoViewmodel) {
      tracking.fireVideoImpression(videoData, `content.${this.type}`);
    }

    useWebp(imageUrl: string) {
      return imageUrl.includes('pdcontent_free');
    }
  }
</script>
